import React, { useState, useEffect } from 'react';
import { Shield, DollarSign, Settings, Save, Plus, Edit, Trash2, AlertTriangle, CheckCircle, Search, Filter, X } from 'lucide-react';
import { securityAPI, securityFeeConfigAPI } from '../services/api';

const ManagementSecurityFee = () => {
  const [feeConfigurations, setFeeConfigurations] = useState([]);
  const [operators, setOperators] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filterOperator, setFilterOperator] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [isCollected, setIsCollected] = useState("all");
  const [statistics, setStatistics] = useState({});
  const [currentSecurityFee, setCurrentSecurityFee] = useState({});
  const [securityFeeList, setSecurityFeeList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [newConfig, setNewConfig] = useState({
    operator_id: '',
    serviceType: '',
    feeAmount: '',
    currency: 'KIP',
    frequency: 'Monthly',
    status: 'Active'
  });

  const handleSearchClick = () => {
    setSearchTerm(searchInput.trim());
    // setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    // setCurrentPage(1);
  };

  const handleOperatorFilter = (value) => {
    setFilterOperator(value);
    // setCurrentPage(1);
  };

  const handleStatusFilter = (value) => {
    setFilterStatus(value);
    // setCurrentPage(1);
  };

  const fetchCurrentSecurityFee = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await securityAPI.getCurrentSecurityFee({isCollected, operator: filterOperator});
      setCurrentSecurityFee(response.data);
    } catch (error) {
      console.error('Error fetching current security fee:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCurrentSecurityFee();
  }, [isCollected, filterOperator]);

  const fetchSecurityFeeList = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await securityAPI.getSecurityFeeList({ isCollected, operator: filterOperator, search: searchTerm, page: currentPage, limit: pageSize });
      const raw = response?.data;
      const list = Array.isArray(raw) ? raw : (raw?.data ?? raw?.transactions ?? []);
      setSecurityFeeList(list);
    } catch (error) {
      console.error('Error fetching security fee list:', error);
      setSecurityFeeList([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSecurityFeeList();
  }, [isCollected, filterOperator, searchTerm, currentPage, pageSize]);

  



  // Fetch security fee configurations (normalize wrapped/unwrapped and camelCase/snake_case)
  // const fetchConfigurations = async () => {
  //   setLoading(true);
  //   setError(null);
  //   try {
  //     const response = await securityFeeConfigAPI.getAll();
  //     const rawConfigurations = response?.data?.configurations ?? response?.data?.data?.configurations ?? response?.configurations ?? [];
  //     const rawStatistics = response?.data?.statistics ?? response?.data?.data?.statistics ?? response?.statistics ?? {};
  //     const configurations = (Array.isArray(rawConfigurations) ? rawConfigurations : []).map((config) => ({
  //       operator_id: config.operator_id ?? config.id ?? config.operator_name ?? config.operator,
  //       operator: config.operator ?? config.operator_name,
  //       serviceType: config.serviceType ?? config.service_type,
  //       feeAmount: config.feeAmount ?? config.fee_amount,
  //       frequency: config.frequency,
  //       status: config.status,
  //       collectionsThisMonth: config.collectionsThisMonth ?? config.collections_this_month,
  //       subscribersAffected: config.subscribersAffected ?? config.subscribers_affected
  //     }));
  //     const statistics = {
  //       totalCollections: rawStatistics.totalCollections ?? rawStatistics.total_collections,
  //       totalSubscribers: rawStatistics.totalSubscribers ?? rawStatistics.total_subscribers,
  //       activeConfigs: rawStatistics.activeConfigs ?? rawStatistics.active_configs,
  //       averageFeeAmount: rawStatistics.averageFeeAmount ?? rawStatistics.average_fee_amount
  //     };
  //     setFeeConfigurations(configurations);
  //     setStatistics(statistics);
  //   } catch (error) {
  //     console.error('Error fetching security fee configurations:', error);
  //     setError('Failed to load security fee configurations. Please ensure the server is running.');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Fetch operators for dropdown (normalize array/object and operator_id/operator_name or id/name or string)
  const fetchOperators = async () => {
    try {
      const response = await securityFeeConfigAPI.getOperators();
      const raw = response?.data ?? response;
      let list = Array.isArray(raw) ? raw : (raw?.data && Array.isArray(raw.data) ? raw.data : []);
      const normalized = list.map((item) => {
        if (typeof item === 'string') {
          return { operator_id: item, operator_name: item };
        }
        return {
          operator_id: item.operator_id ?? item.id ?? item,
          operator_name: item.operator_name ?? item.name ?? item
        };
      }).filter((o) => o.operator_id != null && o.operator_id !== '' && o.operator_name != null && o.operator_name !== '');
      setOperators(normalized);
    } catch (error) {
      console.error('Error fetching operators:', error);
    }
  };

  useEffect(() => {
    fetchOperators();
    // fetchConfigurations();
  }, []);

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'active':
      case 'collected':
      case 'paid':
        return 'text-green-600 bg-green-100';
      case 'inactive':
      case 'cancelled':
      case 'failed':
        return 'text-red-600 bg-red-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (value) => {
    if (!value) return '—';
    try {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return '—';
    }
  };

  const handleAddConfig = async () => {
    if (newConfig.operator_id && newConfig.serviceType && newConfig.feeAmount) {
      try {
        // API accepts camelCase (operator_id, serviceType, feeAmount, etc.); convert to snake_case in API layer if backend requires it
        await securityFeeConfigAPI.create({
          operator_id: newConfig.operator_id,
          serviceType: newConfig.serviceType,
          feeAmount: parseFloat(newConfig.feeAmount),
          currency: newConfig.currency,
          frequency: newConfig.frequency,
          status: newConfig.status
        });

        // Refresh configurations
        // fetchConfigurations();

        // Reset form
        setNewConfig({
          operator_id: '',
          serviceType: '',
          feeAmount: '',
          currency: 'KIP',
          frequency: 'Monthly',
          status: 'Active'
        });
        setShowAddForm(false);
      } catch (error) {
        console.error('Error adding configuration:', error);
        setError('Failed to add configuration');
      }
    }
  };

  // Statistics are now fetched from API
  const totalCollections = statistics.totalCollections || 0;
  const totalSubscribers = statistics.totalSubscribers || 0;
  const activeConfigs = statistics.activeConfigs || 0;
  const averageFeeAmount = statistics.averageFeeAmount || 0;

  return (
    <main className="min-h-screen bg-gray-50">
      <div className="py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Shield className="h-8 w-8 mr-3 text-blue-600" />
              Security Fee Management
            </h1>
            <p className="text-gray-600 mt-2">Configure and manage security fee structures</p>
          </div>
          {/* <button
            onClick={() => setShowAddForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Plus className="h-5 w-5" />
            <span>Add Fee Configuration</span>
          </button> */}
        </div>

        {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => {
                  setError(null);
                  // fetchConfigurations();
                }}
                className="text-red-700 hover:text-red-900 text-sm underline"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md flex gap-2">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search by MSISDN or phone number..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearchClick()}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <button
                type="button"
                onClick={handleSearchClick}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 whitespace-nowrap"
              >
                <Search className="h-5 w-5 mr-2" />
                Search
              </button>
              {searchTerm && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 whitespace-nowrap"
                >
                  <X className="h-5 w-5 mr-2" />
                  Clear
                </button>
              )}
            </div>

            {/* Filters */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="h-5 w-5 text-gray-400" />
                <select
                  value={filterOperator}
                  onChange={(e) => handleOperatorFilter(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Operators</option>
                  {operators.map((operator) => (
                    <option
                      key={operator.operator_id}
                      value={operator.operator_id}
                    >
                      {operator.operator_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <select
                  value={isCollected}
                  onChange={(e) => setIsCollected(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All</option>
                  <option value="collected">Collected</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <select
                  value={filterStatus}
                  onChange={(e) => handleStatusFilter(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Status</option>
                  <option value="ACTIVE">Active</option>
                  <option value="SUSPEND">Suspended</option>
                  <option value="DEACTIVE">Deactive</option>
                </select>
              </div>
            </div>
          </div>
        </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{currentSecurityFee?.total_collected_amount?.toLocaleString()} ₭</p>
            </div>
          </div>
        </div>

        {/* <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Settings className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Configurations</p>
              <p className="text-2xl font-bold text-gray-900">{activeConfigs}</p>
            </div>
          </div>
        </div> */}

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Shield className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Subscribers Affected</p>
              <p className="text-2xl font-bold text-gray-900">{currentSecurityFee?.total_transactions?.toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg Fee Amount</p>
              <p className="text-2xl font-bold text-gray-900">
                {averageFeeAmount.toLocaleString()} ₭
              </p>
            </div>
          </div>
        </div> */}
      </div>

      {/* Add Configuration Form */}
      {/* {showAddForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Add New Fee Configuration</h3>
            <button
              onClick={() => setShowAddForm(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Operator</label>
              <select
                value={newConfig.operator_id}
                onChange={(e) => setNewConfig({ ...newConfig, operator_id: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="">Select Operator</option>
                {operators.map((operator) => (
                  <option key={operator.operator_id} value={operator.operator_id}>
                    {operator.operator_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Service Type</label>
              <select
                value={newConfig.serviceType}
                onChange={(e) => {
                  const serviceType = e.target.value;
                  const feeAmount = serviceType === 'mbb' ? '3000' : serviceType === 'fbb' ? '5000' : '';
                  setNewConfig({ ...newConfig, serviceType, feeAmount });
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="">Select Service</option>
                <option value="mbb">MBB (Mobile Broadband) - 3000 KIP</option>
                <option value="fbb">FBB (Fixed Broadband/PSTN) - 5000 KIP</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fee Amount</label>
              <input
                type="number"
                step="0.01"
                value={newConfig.feeAmount}
                onChange={(e) => setNewConfig({ ...newConfig, feeAmount: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <select
                value={newConfig.currency}
                onChange={(e) => setNewConfig({ ...newConfig, currency: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="KIP">KIP (₭)</option>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
              <select
                value={newConfig.frequency}
                onChange={(e) => setNewConfig({ ...newConfig, frequency: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Annually">Annually</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={newConfig.status}
                onChange={(e) => setNewConfig({ ...newConfig, status: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Pending">Pending</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex justify-end space-x-2">
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAddConfig}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center space-x-2"
            >
              <Save className="h-4 w-4" />
              <span>Save Configuration</span>
            </button>
          </div>
        </div>
      )} */}

      {/* Fee Configurations Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Fees</h3>
        </div>

        {loading ? (
          <div className="p-6 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className="mt-2 text-gray-600">Loading configurations...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MSISDN</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Operator</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscriber type</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Fee / Expected</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Collection date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction ref</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {securityFeeList.map((row) => {
                  const operatorName = operators.find((o) => Number(o.operator_id) === Number(row.operator_id))?.operator_name ?? row.operator_id ?? '—';
                  const feeAmount = row.fee_amount ?? row.feeAmount ?? 0;
                  const expectedAmount = row.expected_amount ?? row.expectedAmount ?? feeAmount;
                  return (
                    <tr key={row.id ?? `${row.msisdn}-${row.collection_date}-${row.transaction_ref}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.msisdn ?? '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{operatorName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {row.subscriber_type === 'mbb' ? 'MBB' : row.subscriber_type === 'fbb' ? 'FBB' : row.subscriber_type ?? '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right tabular-nums text-gray-900">
                        {Number(feeAmount).toLocaleString()} / {Number(expectedAmount).toLocaleString()} ₭
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(row.collection_date)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(row.due_date)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${getStatusColor(row.status)}`}>
                          {row.status ?? '—'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{row.transaction_ref ?? '—'}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={row.notes ?? ''}>{row.notes ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {securityFeeList.length === 0 && !loading && (
          <div className="p-6 text-center text-gray-500">
            <Shield className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p>No security fee records found.</p>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      {/* <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="flex items-center justify-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
            <span className="text-sm font-medium">Activate All Pending</span>
          </button>
          <button className="flex items-center justify-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
            <span className="text-sm font-medium">Review Low Collections</span>
          </button>
          <button className="flex items-center justify-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <DollarSign className="h-5 w-5 text-blue-600 mr-2" />
            <span className="text-sm font-medium">Export Fee Report</span>
          </button>
        </div>
      </div> */}
      </div>
    </main>
  );
};

export default ManagementSecurityFee;